import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup.types'
import type { EnabledFilters } from 'components/Search/type'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getRankedPopularFilterItems, isRankedPopularFilterChecked } from './utils/rankedPopularFilters'

interface Params {
  popularFilters: Array<App.RankedPopularFilter> | undefined
  availableFilters: App.OfferListAvailableFilters | undefined
  enabledFilters: EnabledFilters
  filters: App.OfferListFilters
  canShow: boolean
}

function useRankedPopularFilters(params: Params) {
  const { popularFilters, availableFilters, enabledFilters, filters, canShow } = params
  const intl = useIntl()
  const { CUSTOMER_RATING_GTE } = useCustomerRatingOptions()

  const rankedItems = useMemo(() => (
    canShow ? getRankedPopularFilterItems(popularFilters, availableFilters, enabledFilters) : EmptyArray
  ), [canShow, popularFilters, availableFilters, enabledFilters])

  const { isAnyVariant } = useExperiment(ChertoExperiments.searchPopularFilters, {
    canActivate: rankedItems.length > 0,
  })

  const exceptionalRatingLabel = intl.formatMessage({
    id: 'TRH7ufj32jUujoFNDitO',
    defaultMessage: 'Exceptional Rating: 9+',
    description: 'Label for the exceptional customer rating option in the popular filters group of the hotel search filter pane',
  })

  const items = useMemo<Array<FilterPanelCheckItem>>(() => {
    if (!isAnyVariant) return EmptyArray

    return rankedItems.map(item => ({
      value: item.value,
      label: item.group === 'customerRatings' && item.value === CUSTOMER_RATING_GTE.EXCEPTIONAL.value ? exceptionalRatingLabel : item.label,
      count: item.count,
      checked: isRankedPopularFilterChecked(item, filters),
      hideCount: true,
    }))
  }, [isAnyVariant, rankedItems, exceptionalRatingLabel, CUSTOMER_RATING_GTE, filters])

  const itemByValue = useMemo(() => (
    new Map(rankedItems.map(item => [String(item.value), item]))
  ), [rankedItems])

  return { items, itemByValue }
}

export default useRankedPopularFilters
